﻿import type { SipClient, InitOptions, PbxConfig, Events } from "../api";
import {
    UserAgent,
    UserAgentState,
    Registerer,
    RegistererState,
    RequestPendingError,
    Inviter,
    Invitation,
    SessionState,
    Subscriber,
    SubscriptionState,
} from "sip.js";
import { callerDisplayName, remoteCallerDisplayName } from "../callerIdentity";

// Chrome can leave ICE gathering in "gathering" until SIP.js's default
// five-second timeout even after it has produced candidates which are sufficient
// for this PBX. Keep a bounded gathering window so SDP still contains candidates
// without holding INVITE/200 OK signalling for five seconds.
const SIP_ICE_GATHERING_TIMEOUT_MS = 1000;

// SIP.js can report SessionState.Terminated immediately before invoking the
// INVITE request delegate's onReject callback. Give that callback one browser
// turn to attach the PBX status code/reason before publishing the ended event.
const OUTBOUND_REJECTION_DETAIL_GRACE_MS = 75;

// tiny event helper (same pattern as NoopClient)
function emit<E extends keyof Events>(
    ev: E,
    bus?: Partial<Events>,
    payload?: Parameters<NonNullable<Events[E]>>[0]
) {
    const h = bus?.[ev] as any;
    try {
        h?.(payload);
    } catch (err) {
        console.error("ETS.SipClient handler error", err);
    }
}

/**
 * Real SIP.js-backed client.
 *
 * Transport lifecycle + registration + basic calling (dial/answer/decline/hangup) + DTMF.
 */
export class RealSipClient implements SipClient {
    private events?: Partial<Events>;
    private audioId?: string;
    private ua?: UserAgent;
    private lastCfg?: PbxConfig;

    private reg?: Registerer;
    private regState?: RegistererState;

    private lastTransportState?: string;
    private lastRegUiState?: "unregistered" | "registering" | "registered";
    private unregisterRequested = false;

    // Captured details from the last REGISTER rejection (e.g. 401/403)
    private lastRegisterRejectMessage?: string;
    private lastRegisterRejectCode?: number;
    private lastRegisterRejectReason?: string;

    // Active outbound calls keyed by our generated callId
    private calls = new Map<string, Inviter>();

    // Active inbound calls keyed by callId
    private incomingCalls = new Map<string, Invitation>();
    private answerInFlight = new Map<string, Promise<boolean>>();
    private answerStartedAt = new Map<string, number>();
    private attendedCancelInFlight = new Map<string, Promise<boolean>>();
    private terminatingCallIds = new Set<string>();

    private blfSubscribers = new Map<string, Subscriber>();

    private markAnswerTiming(event: string, callId: string, session?: any, details?: Record<string, unknown>): void {
        const sessionId = String(
            session?.id ??
            session?.request?.callId ??
            session?.request?.callIdHeader ??
            callId ??
            "unknown"
        );

        try {
            (globalThis as any)?.ETS?.AnswerTiming?.mark?.(event, {
                callId,
                sessionId,
                ...details
            });
        } catch {
            // Timing diagnostics must never interfere with call handling.
        }
    }

    private voicemailSubscriber?: Subscriber;

    // Prevent concurrent connect() calls from killing the UA mid-handshake
    private connectInFlight?: Promise<void>;
    private connectRequestedCfgKey?: string;
    private connectOperation = 0;
    private transportEpoch = 0;
    private registrationTransportEpoch = -1;
    private registrationInFlight?: Promise<void>;
    private cancelRegistrationAttempt?: () => void;
    private resetInFlight?: Promise<void>;

    // Track which call is currently "active" in the UI
    private activeCallId?: string;
    private activeCallTransition: Promise<void> = Promise.resolve();

    // Cache last-known UI state per call so we can re-emit when toggling active
    private callMeta = new Map<
        string,
        { state: "incoming" | "establishing" | "active" | "held" | "ended"; remoteUri?: string | null; remoteDisplayName?: string }
        >();

    private async runActiveCallTransition<T>(operation: () => Promise<T>): Promise<T> {
        const previousTransition = this.activeCallTransition;
        let releaseTransition!: () => void;
        this.activeCallTransition = new Promise<void>(resolve => {
            releaseTransition = resolve;
        });

        await previousTransition;
        try {
            return await operation();
        } finally {
            releaseTransition();
        }
    }

    private otherActiveCallIds(nextCallId: string): string[] {
        const activeIds = new Set<string>();
        if (this.activeCallId && this.activeCallId !== nextCallId) {
            activeIds.add(this.activeCallId);
        }

        for (const [callId, meta] of this.callMeta.entries()) {
            if (callId !== nextCallId && meta.state === "active") {
                activeIds.add(callId);
            }
        }

        return [...activeIds];
    }

    private async holdOtherActiveCalls(nextCallId: string): Promise<string[] | null> {
        const heldCallIds: string[] = [];
        for (const callId of this.otherActiveCallIds(nextCallId)) {
            if (!await this.hold(callId)) {
                console.error("[ETS] could not activate call because another call could not be held", {
                    nextCallId,
                    activeCallId: callId
                });
                return null;
            }
            heldCallIds.push(callId);
        }

        return heldCallIds;
    }

    private async restorePreviouslyActiveCall(heldCallIds: string[]): Promise<void> {
        for (let index = heldCallIds.length - 1; index >= 0; index -= 1) {
            const callId = heldCallIds[index];
            if (await this.resumeSession(callId)) {
                return;
            }
        }
    }

    private mapDialogNotifyToBlfState(body: string): string {
        const text = (body || "").toLowerCase();

        if (!text.trim()) return "Unknown";

        if (
            text.includes("<state>early</state>") ||
            text.includes("direction=\"initiator\"") ||
            text.includes("direction=\"recipient\"")
        ) {
            return "Ringing";
        }

        if (
            text.includes("<state>confirmed</state>") ||
            text.includes("<state>trying</state>") ||
            text.includes("<state>proceeding</state>")
        ) {
            return "Busy";
        }

        if (
            text.includes("<state>terminated</state>") ||
            text.includes("state=\"full\"") ||
            text.includes("state=\"partial\"")
        ) {
            return "Available";
        }

        return "Unknown";
    }

    // Local ringback tone (generated) so UI feels responsive even without early media
    private ringbackByCallId = new Map<string, { ctx: AudioContext; osc1: OscillatorNode; osc2: OscillatorNode; gain: GainNode; timer?: number }>();
    private pcDiagnosticsWired = new WeakSet<RTCPeerConnection>();
    private earlyMediaFallbackTimers = new Map<string, number>();
    private outboundEarlyMediaCalls = new Set<string>();

    private startLocalRingback(callId: string, reason = "progress-without-early-media") {
        // already running?
        if (this.ringbackByCallId.has(callId)) {
            console.info("[ETS][early-media] local ringback already running", { callId, reason });
            return;
        }

        try {
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();

            const gain = ctx.createGain();
            gain.gain.value = 0.0; // start muted; we'll ramp
            gain.connect(ctx.destination);

            const osc1 = ctx.createOscillator();
            osc1.type = "sine";
            osc1.frequency.value = 400;
            osc1.connect(gain);
            osc1.start();

            const osc2 = ctx.createOscillator();
            osc2.type = "sine";
            osc2.frequency.value = 450;
            osc2.connect(gain);
            osc2.start();

            // UK ringback cadence: 400+450Hz, 0.4s on, 0.2s off, 0.4s on, 2s off.
            const ring = () => {
                const now = ctx.currentTime;
                gain.gain.cancelScheduledValues(now);
                gain.gain.setValueAtTime(0.0, now);
                gain.gain.linearRampToValueAtTime(0.15, now + 0.05);
                gain.gain.setValueAtTime(0.15, now + 0.4);
                gain.gain.linearRampToValueAtTime(0.0, now + 0.45);
                gain.gain.setValueAtTime(0.0, now + 0.6);
                gain.gain.linearRampToValueAtTime(0.15, now + 0.65);
                gain.gain.setValueAtTime(0.15, now + 1.0);
                gain.gain.linearRampToValueAtTime(0.0, now + 1.05);
            };

            ring();
            const timer = window.setInterval(ring, 3000);

            this.ringbackByCallId.set(callId, { ctx, osc1, osc2, gain, timer });
            console.info("[ETS][early-media] local ringback fallback started", {
                callId,
                reason,
                localFallback: true,
                tone: "UK ringback 400+450Hz",
                cadence: "0.4s on, 0.2s off, 0.4s on, 2s off",
                audioContextState: ctx.state,
                destinationChannels: ctx.destination?.channelCount ?? null
            });
        } catch (err) {
            console.warn("[ETS][early-media] local ringback fallback start failed", { callId, reason, err });
        }
    }

    private stopLocalRingback(callId: string, reason = "stop-requested") {
        const rb = this.ringbackByCallId.get(callId);
        if (!rb) {
            console.info("[ETS][early-media] local ringback stop ignored; not running", { callId, reason });
            return;
        }

        try {
            if (rb.timer) window.clearInterval(rb.timer);
            rb.osc1.stop();
            rb.osc2.stop();
            rb.osc1.disconnect();
            rb.osc2.disconnect();
            rb.gain.disconnect();
            rb.ctx.close?.();
        } catch {
            /* ignore */
        } finally {
            this.ringbackByCallId.delete(callId);
            console.info("[ETS][early-media] local ringback fallback stopped", { callId, reason });
        }
    }

    async setMicrophoneMuted(callId: string, muted: boolean): Promise<boolean> {
        const session = this.calls.get(callId);
        const pc: RTCPeerConnection | undefined = (session as any)?.sessionDescriptionHandler?.peerConnection;
        const audioSenders = pc?.getSenders?.().filter(sender => sender.track?.kind === "audio") ?? [];

        if (!session || !pc || audioSenders.length === 0) {
            console.warn("[ETS][audio] setMicrophoneMuted failed; no active audio sender", {
                callId,
                muted,
                hasSession: !!session,
                hasPeerConnection: !!pc,
                senderCount: audioSenders.length
            });
            return false;
        }

        for (const sender of audioSenders) {
            if (sender.track) sender.track.enabled = !muted;
        }

        console.info("[ETS][audio] setMicrophoneMuted applied", {
            callId,
            muted,
            senders: audioSenders.map(sender => ({
                trackKind: sender.track?.kind ?? null,
                trackId: sender.track?.id ?? null,
                enabled: sender.track?.enabled ?? null,
                readyState: sender.track?.readyState ?? null
            }))
        });
        return true;
    }

    private clearEarlyMediaFallbackTimer(callId: string, reason: string) {
        const timer = this.earlyMediaFallbackTimers.get(callId);
        if (!timer) return;

        window.clearTimeout(timer);
        this.earlyMediaFallbackTimers.delete(callId);
        console.info("[ETS][early-media] early media fallback timer cleared", { callId, reason });
    }

    private scheduleEarlyMediaFallbackCheck(session: any, callId: string, context: string, delayMs = 3000) {
        this.clearEarlyMediaFallbackTimer(callId, `reschedule:${context}`);

        const timer = window.setTimeout(() => {
            this.earlyMediaFallbackTimers.delete(callId);

            const pc: RTCPeerConnection | undefined = session?.sessionDescriptionHandler?.peerConnection;
            const receivers = pc?.getReceivers?.() ?? [];
            const receiverTracks = receivers
                .map((r: RTCRtpReceiver) => r.track)
                .filter((track: MediaStreamTrack | null | undefined): track is MediaStreamTrack => !!track);

            const hasUnmutedLiveTrack = receiverTracks.some(track => track.readyState === "live" && !track.muted);
            const icePlayable = pc?.iceConnectionState === "connected" || pc?.iceConnectionState === "completed";
            const connectionPlayable = pc?.connectionState === "connected";
            const hasRemoteDescription = !!pc?.remoteDescription;
            const shouldFallback = !hasUnmutedLiveTrack && !icePlayable && !connectionPlayable;

            console.info("[ETS][early-media] early media audibility check", {
                callId,
                context,
                delayMs,
                shouldFallback,
                localFallbackWillStart: shouldFallback,
                hasRemoteDescription,
                hasUnmutedLiveTrack,
                iceConnectionState: pc?.iceConnectionState ?? null,
                connectionState: pc?.connectionState ?? null,
                receiverTracks: receiverTracks.map(track => ({
                    kind: track.kind,
                    id: track.id,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState
                })),
                pc: this.describePeerConnection(pc)
            });

            if (shouldFallback) {
                console.warn("[ETS][early-media] early media SDP received but no playable audio detected; starting local ringback fallback", {
                    callId,
                    context,
                    localFallback: true,
                    hasRemoteDescription,
                    iceConnectionState: pc?.iceConnectionState ?? null,
                    connectionState: pc?.connectionState ?? null
                });
                this.startLocalRingback(callId, `${context}-sdp-not-playable`);
            }
        }, delayMs);

        this.earlyMediaFallbackTimers.set(callId, timer);
        console.info("[ETS][early-media] early media fallback timer scheduled", { callId, context, delayMs });
    }

    init(opts?: InitOptions): void {
        this.events = opts?.events;
        this.audioId = opts?.audioElementId;
        console.info("[ETS] RealSipClient.init", { audioId: this.audioId });

        // Keep initial states consistent with NoopClient
        this.emitTransport("disconnected");
        this.emitRegistration("unregistered");
    }
    async watchBlf(extensions: string[]): Promise<boolean> {
        const clean = Array.from(
            new Set(
                (extensions || [])
                    .map(x => String(x || "").trim())
                    .filter(x => x.length > 0)
            )
        );

        console.info("[ETS] watchBlf() requested", { extensions: clean });

        if (!this.ua) {
            console.warn("[ETS] watchBlf ignored; UserAgent not started");
            return false;
        }

        const domain = this.getDomainFromSipUri(this.lastCfg?.sipUri);
        if (!domain) {
            console.warn("[ETS] watchBlf ignored; could not resolve SIP domain");
            return false;
        }

        for (const ext of clean) {
            if (this.blfSubscribers.has(ext)) {
                console.info("[ETS] BLF already subscribed", { ext });
                continue;
            }

            const target = UserAgent.makeURI(`sip:${ext}@${domain}`);
            if (!target) {
                console.warn("[ETS] BLF target URI invalid", { ext, domain });
                continue;
            }

            const subscriber = new Subscriber(this.ua, target, "dialog");

            subscriber.delegate = {
                onNotify: (notification: any) => {
                    try {
                        notification.accept?.();

                        const body =
                            notification?.request?.body ??
                            notification?.message?.body ??
                            "";

                        console.info("[ETS] BLF NOTIFY", {
                            ext,
                            body
                        });

                        const mapped = this.mapDialogNotifyToBlfState(String(body || ""));

                        emit("blfChanged", this.events, {
                            extension: ext,
                            blfState: mapped,
                            presenceState: mapped,
                            eventType: "dialog",
                            rawBody: body
                        } as any);
                    } catch (err) {
                        console.warn("[ETS] BLF NOTIFY handling failed", { ext, err });
                    }
                }
            };

            subscriber.stateChange.addListener((state: any) => {
                console.info("[ETS] BLF subscription state", {
                    ext,
                    state: String(state)
                });

                if (state === SubscriptionState.Terminated) {
                    this.blfSubscribers.delete(ext);

                    emit("blfChanged", this.events, {
                        extension: ext,
                        blfState: "Unknown",
                        presenceState: "Unknown",
                        eventType: "dialog-terminated"
                    } as any);
                }
            });

            try {
                this.blfSubscribers.set(ext, subscriber);

                await subscriber.subscribe({
                    requestOptions: {
                        extraHeaders: [
                            "Accept: application/dialog-info+xml"
                        ]
                    }
                } as any);

                emit("blfChanged", this.events, {
                    extension: ext,
                    blfState: "Unknown",
                    presenceState: "Unknown",
                    eventType: "dialog-subscribed"
                } as any);
            } catch (err) {
                this.blfSubscribers.delete(ext);
                console.warn("[ETS] BLF subscribe failed", { ext, err });

                emit("blfChanged", this.events, {
                    extension: ext,
                    blfState: "Unknown",
                    presenceState: "SubscribeFailed",
                    eventType: "dialog-subscribe-failed"
                } as any);
            }
        }

        return true;
    }
    async refreshVoicemail(): Promise<boolean> {
        if (!this.ua || this.regState !== RegistererState.Registered) {
            return false;
        }

        await this.unsubscribeVoicemail();
        await this.subscribeVoicemail();
        return true;
    }
    async connect(cfg: PbxConfig, opt?: { register?: boolean }): Promise<void> {
        const cfgKey = JSON.stringify([cfg.wssUrl, cfg.sipUri, cfg.authUser, cfg.password]);
        if (this.connectInFlight && this.connectRequestedCfgKey === cfgKey)
            return this.connectInFlight;

        const operation = ++this.connectOperation;
        this.connectRequestedCfgKey = cfgKey;
        const pending = (async () => {
            if (this.resetInFlight) await this.resetInFlight;
            if (operation !== this.connectOperation) throw new Error("Connection request superseded");
            if (this.ua && !this.isSameCfg(this.lastCfg, cfg))
                await this.hardReset("connection profile changed", false);
            if (operation !== this.connectOperation) throw new Error("Connection request superseded");

            this.lastCfg = { ...cfg };
            let ua = this.ua;
            if (!ua) {
                const uri = UserAgent.makeURI(cfg.sipUri);
                if (!uri) {
                    this.emitTransport("disconnected", "Invalid sipUri");
                    throw new Error("Invalid SIP URI");
                }
                ua = new UserAgent({
                    uri,
                    displayName: callerDisplayName(uri, cfg.authUser),
                    authorizationUsername: cfg.authUser,
                    authorizationPassword: cfg.password,
                    logBuiltinEnabled: false,
                    logLevel: "warn",
                    transportOptions: { server: cfg.wssUrl },
                });
                this.ua = ua;
                this.wireIncoming(ua);
                this.wireTransport(ua);
            }
            if (ua.isConnected()) return;
            this.emitTransport("connecting");
            try {
                // A dropped socket does not stop the UserAgent. start() is a
                // no-op in Started state; reconnect() restores that transport
                // without terminating its active, held or incoming sessions.
                if (ua.state === UserAgentState.Started) await ua.reconnect();
                else await ua.start();
                if (operation !== this.connectOperation || this.ua !== ua)
                    throw new Error("Connection request superseded");
                if (!ua.isConnected()) throw new Error("Transport did not connect");
                this.emitTransport("connected");
            } catch (error) {
                if (operation === this.connectOperation && this.ua === ua)
                    this.emitTransport("disconnected", "Transport connection failed");
                throw error;
            }
        })();
        this.connectInFlight = pending;
        try { await pending; }
        finally {
            if (this.connectInFlight === pending) {
                this.connectInFlight = undefined;
                this.connectRequestedCfgKey = undefined;
            }
        }
    }

    async disconnect(): Promise<void> {
        await this.hardReset("disconnect requested");
    }

    // --- Calling surface ---

    private getPreferredInputDeviceId(): string {
        try {
            const sipClientGlobal = (globalThis as any)?.ETS?.SipClient;
            const fromGetter = sipClientGlobal?.getPreferredInputDevice?.();
            const fromProperty = sipClientGlobal?.preferredMicDeviceId;
            const fromSession = globalThis.sessionStorage?.getItem?.("ETS.preferredMic");
            return String(fromGetter || fromProperty || fromSession || "").trim();
        } catch {
            return "";
        }
    }

    private buildAudioConstraints(): MediaTrackConstraints | boolean {
        const deviceId = this.getPreferredInputDeviceId();
        const base: MediaTrackConstraints = {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: false,
        };

        if (deviceId) {
            return {
                ...base,
                deviceId: { exact: deviceId },
            };
        }

        return base;
    }

    private buildSessionDescriptionHandlerOptions(context: string, callId: string) {
        const preferredMicDeviceId = this.getPreferredInputDeviceId();
        const constraints = {
            audio: this.buildAudioConstraints(),
            video: false,
        };

        console.info("[ETS][audio] media constraints prepared", {
            context,
            callId,
            preferredMic: preferredMicDeviceId ? "set" : "default",
            constraints
        });

        return {
            constraints,
            iceGatheringTimeout: SIP_ICE_GATHERING_TIMEOUT_MS,
        };
    }

    async dial(targetUri: string): Promise<string> {
        const callId = this.newCallId();
        let inviteFailure: { code?: number; reason?: string } | undefined;
        let terminationTimer: number | undefined;
        let terminationEmitted = false;
        let inviteAccepted = false;
        let answered = false;

        if (!this.ua) {
            this.emitCallUpdated(callId, "ended", targetUri, false);
            throw new Error("Cannot dial: UserAgent not started. Call connect() first.");
        }

        const uri = this.buildTargetUri(targetUri);
        if (!uri) {
            this.emitCallUpdated(callId, "ended", targetUri, false);
            throw new Error(`Invalid dial target: '${targetUri}'`);
        }

        const prepared = await this.runActiveCallTransition(async () => {
            const heldCallIds = await this.holdOtherActiveCalls(callId);
            if (heldCallIds === null) {
                return false;
            }

            this.emitCallUpdated(callId, "establishing", targetUri, true);
            return true;
        });

        if (!prepared) {
            throw new Error("Unable to start a new call because the current call could not be placed on hold.");
        }

        console.info("[ETS] dial() [real]", {
            callId,
            targetUri,
            uri: uri.toString(),
        });

        const inviter = new Inviter(this.ua, uri, {
            sessionDescriptionHandlerOptions: this.buildSessionDescriptionHandlerOptions("dial", callId),
        } as any);

        this.calls.set(callId, inviter);

        const publishAnswered = () => {
            // Media/session establishment alone is not evidence of an answer:
            // provisional SDP can establish early media before the final INVITE response.
            if (!inviteAccepted || answered || terminationEmitted || terminationTimer !== undefined ||
                this.terminatingCallIds.has(callId) || inviter.state !== SessionState.Established)
                return;

            answered = true;
            this.outboundEarlyMediaCalls.delete(callId);
            this.clearEarlyMediaFallbackTimer(callId, "invite-accepted");
            this.stopLocalRingback(callId, "invite-accepted");
            this.emitCallUpdated(callId, "active", targetUri, true, undefined, undefined, true);
        };

        const completeTerminated = () => {
            if (terminationEmitted) return;
            terminationEmitted = true;

            if (terminationTimer !== undefined) {
                window.clearTimeout(terminationTimer);
                terminationTimer = undefined;
            }

            this.clearEarlyMediaFallbackTimer(callId, "session-terminated");
            this.stopLocalRingback(callId, "session-terminated");
            this.outboundEarlyMediaCalls.delete(callId);
            this.emitCallUpdated(
                callId,
                "ended",
                targetUri,
                false,
                inviteFailure?.code,
                inviteFailure?.reason,
                answered
            );
            this.calls.delete(callId);
            this.terminatingCallIds.delete(callId);
        };

        (inviter as any).stateChange.addListener((state: any) => {
            console.info("[ETS] call stateChange", { callId, state: String(state) });
            this.logSessionMediaDiagnostics(inviter, callId, `stateChange:${String(state)}`);

            if (terminationEmitted || (terminationTimer !== undefined && state !== SessionState.Terminated))
                return;

            if (this.terminatingCallIds.has(callId) && state !== SessionState.Terminated) {
                console.info("[ETS][transfer][sip] ignored late consult state during termination", {
                    callId,
                    state: String(state)
                });
                return;
            }

            if (state === SessionState.Establishing) {
                if (!inviteAccepted)
                    this.emitCallUpdated(callId, "establishing", targetUri, true);
                return;
            }

            if (state === SessionState.Established) {
                this.clearEarlyMediaFallbackTimer(callId, "session-established");
                this.stopLocalRingback(callId, "session-established");

                try {
                    this.attachRemoteAudio(inviter, callId, "session-established");
                } catch (err) {
                    console.warn("[ETS][early-media] attachRemoteAudio failed after establishment", { callId, err });
                }

                publishAnswered();
                return;
            }

            if (state === SessionState.Terminated) {
                terminationTimer = window.setTimeout(
                    completeTerminated,
                    OUTBOUND_REJECTION_DETAIL_GRACE_MS);
                return;
            }
        });

        try {
            await inviter.invite({
                requestDelegate: {
                    onAccept: (response: any) => {
                        const code = response?.message?.statusCode ?? response?.statusCode;
                        if (code < 200 || code >= 300 || !Number.isInteger(code) || terminationEmitted || terminationTimer !== undefined ||
                            this.terminatingCallIds.has(callId))
                            return;

                        inviteAccepted = true;
                        publishAnswered();
                    },
                    onProgress: (response: any) => {
                        if (inviteAccepted || terminationEmitted || terminationTimer !== undefined || this.terminatingCallIds.has(callId)) {
                            console.info("[ETS][sip] ignored late outbound progress", { callId });
                            return;
                        }

                        const code = response?.message?.statusCode ?? response?.statusCode;
                        const reason = response?.message?.reasonPhrase ?? response?.reasonPhrase;
                        const body = this.getSipMessageBody(response);
                        const contentType = this.getSipMessageHeader(response, "Content-Type");
                        const hasSdp = this.looksLikeSdp(body, contentType);

                        // 180 Ringing / 183 Session Progress etc.
                        console.info("[ETS][early-media] INVITE progress response", {
                            callId,
                            code,
                            reason,
                            contentType,
                            hasBody: body.length > 0,
                            bodyLength: body.length,
                            hasSdp,
                            sdp: hasSdp ? this.summariseSdp(body) : null
                        });
                        this.logSessionMediaDiagnostics(inviter, callId, `progress:${code}`);

                        // If we get a 180, we should present "ringing" to the user (local ringback).
                        if (code === 180) {
                            if (hasSdp) {
                                this.stopLocalRingback(callId, "180-with-sdp");
                                this.attachRemoteAudio(inviter, callId, "180-ringing-with-sdp");
                                this.scheduleEarlyMediaFallbackCheck(inviter, callId, "180-ringing-with-sdp");
                            } else {
                                this.startLocalRingback(callId, "180-without-sdp");
                            }

                            // You don't have a "ringing" UI state yet, so keep "establishing"
                            // but this is the moment your UI can show "Ringing…" if you want later.
                            this.emitCallUpdated(callId, "establishing", targetUri, true);
                        }

                        // 183 may include early media; for now, we’ll treat it as progress.
                        if (code === 183) {
                            if (hasSdp) {
                                this.outboundEarlyMediaCalls.add(callId);
                                this.stopLocalRingback(callId, "183-with-sdp");
                                this.attachRemoteAudio(inviter, callId, "183-session-progress-with-sdp");
                                this.scheduleEarlyMediaFallbackCheck(inviter, callId, "183-session-progress-with-sdp");
                            } else {
                                console.warn("[ETS][early-media] 183 Session Progress did not include SDP; early media cannot be established from this response", {
                                    callId,
                                    code,
                                    reason,
                                    contentType
                                });
                                this.startLocalRingback(callId, "183-without-sdp");
                            }
                            this.emitCallUpdated(callId, "establishing", targetUri, true);
                        }
                    },
                    onReject: (response: any) => {
                        if (inviteAccepted || terminationEmitted)
                            return;
                        const code = response?.message?.statusCode ?? response?.statusCode;
                        const reason = response?.message?.reasonPhrase ?? response?.reasonPhrase;
                        inviteFailure = { code, reason };
                        console.warn("[ETS] INVITE rejected", { callId, code, reason });

                        this.clearEarlyMediaFallbackTimer(callId, "invite-rejected");
                        this.stopLocalRingback(callId, "invite-rejected");
                        this.outboundEarlyMediaCalls.delete(callId);
                    },
                },
            } as any);

            return callId;
        } catch (err: any) {
            const friendlyMessage = this.describeInviteFailure(err);

            console.error("[ETS] inviter.invite() failed", {
                callId,
                err,
                friendlyMessage
            });

            terminationEmitted = true;
            if (terminationTimer !== undefined) {
                window.clearTimeout(terminationTimer);
                terminationTimer = undefined;
            }
            this.clearEarlyMediaFallbackTimer(callId, "invite-failed");
            this.stopLocalRingback(callId, "invite-failed");
            this.outboundEarlyMediaCalls.delete(callId);
            this.emitCallUpdated(callId, "ended", targetUri, false, undefined, friendlyMessage);
            this.calls.delete(callId);

            throw new Error(friendlyMessage);
        }
    }

    private describeInviteFailure(err: any): string {
        const name = String(err?.name ?? "");
        const message = String(err?.message ?? err ?? "");

        if (name === "OverconstrainedError" ||
            message.toLowerCase().includes("invalid constraint") ||
            message.toLowerCase().includes("constraint not satisfied")) {
            return "The selected microphone is no longer available. Choose another microphone in Audio settings, or select the default microphone, then try again.";
        }

        if (name === "NotFoundError" || message.includes("Requested device not found")) {
            return "Unable to make the call because no microphone device was found. Please connect or enable a microphone and try again.";
        }

        if (name === "NotAllowedError" || message.includes("Permission denied")) {
            return "Unable to make the call because microphone access was denied. Please allow microphone access and try again.";
        }

        if (name === "NotReadableError") {
            return "Unable to make the call because the microphone is already in use or cannot be accessed.";
        }

        return message || "Unable to make the call because the browser could not start the audio session.";
    }

    async answer(callId: string): Promise<boolean> {
        const existingAnswer = this.answerInFlight.get(callId);
        if (existingAnswer) {
            console.info("[ETS] answer() already in progress; joining existing request", { callId });
            return existingAnswer;
        }

        const inv = this.incomingCalls.get(callId);
        if (!inv) {
            console.warn("[ETS] answer: no incoming call for id", callId);
            return false;
        }

        const startedAt = performance.now();
        this.answerStartedAt.set(callId, startedAt);
        this.markAnswerTiming("answer entered", callId, inv, {
            invitationState: String((inv as any).state ?? "unknown"),
            preferredMic: this.getPreferredInputDeviceId() ? "set" : "default"
        });
        console.info("[ETS][answer-timing] answer requested", {
            callId,
            invitationState: String((inv as any).state ?? "unknown"),
            preferredMic: this.getPreferredInputDeviceId() ? "set" : "default"
        });

        try {
            const permissionPromise = navigator.permissions?.query?.({ name: "microphone" as PermissionName });
            permissionPromise?.then(permission => {
                console.info("[ETS][answer-timing] microphone permission resolved", {
                    callId,
                    state: permission.state,
                    elapsedMs: this.answerElapsedMs(callId)
                });
            }).catch(err => {
                console.warn("[ETS][answer-timing] microphone permission query failed", {
                    callId,
                    error: err?.message ?? String(err),
                    elapsedMs: this.answerElapsedMs(callId)
                });
            });
        } catch (err: any) {
            console.warn("[ETS][answer-timing] microphone permission query unavailable", {
                callId,
                error: err?.message ?? String(err),
                elapsedMs: this.answerElapsedMs(callId)
            });
        }

        const answerPromise = this.runActiveCallTransition(async () => {
            const heldCallIds = await this.holdOtherActiveCalls(callId);
            if (heldCallIds === null) {
                this.markAnswerTiming("answer blocked because active call could not be held", callId, inv, {
                    answerElapsedMs: this.answerElapsedMs(callId)
                });
                this.answerInFlight.delete(callId);
                this.answerStartedAt.delete(callId);
                return false;
            }

            const slowAnswerTimer = window.setTimeout(() => {
                console.warn("[ETS][answer-timing] invitation accept still pending", {
                    callId,
                    invitationState: String((inv as any).state ?? "unknown"),
                    elapsedMs: this.answerElapsedMs(callId)
                });
                this.logSessionMediaDiagnostics(inv, callId, "answer-still-pending");
            }, 4000);

            try {
                console.info("[ETS][answer-timing] invitation accept starting", {
                    callId,
                    elapsedMs: this.answerElapsedMs(callId)
                });
                this.markAnswerTiming("Invitation.accept() called", callId, inv, {
                    answerElapsedMs: this.answerElapsedMs(callId)
                });

                const acceptPromise = inv.accept({
                    sessionDescriptionHandlerOptions: this.buildSessionDescriptionHandlerOptions("answer", callId),
                } as any);
                this.markAnswerTiming("Invitation.accept() returned", callId, inv, {
                    answerElapsedMs: this.answerElapsedMs(callId),
                    invitationState: String((inv as any).state ?? "unknown")
                });

                await acceptPromise;

                console.info("[ETS][answer-timing] invitation accept completed", {
                    callId,
                    invitationState: String((inv as any).state ?? "unknown"),
                    elapsedMs: this.answerElapsedMs(callId)
                });
                this.markAnswerTiming("Invitation.accept() resolved", callId, inv, {
                    answerElapsedMs: this.answerElapsedMs(callId),
                    invitationState: String((inv as any).state ?? "unknown")
                });
                return true;
            } catch (err: any) {
                console.error("[ETS][answer-timing] invitation accept failed", {
                    callId,
                    err: err?.message ?? String(err),
                    elapsedMs: this.answerElapsedMs(callId)
                });
                this.markAnswerTiming("Invitation.accept() failed", callId, inv, {
                    answerElapsedMs: this.answerElapsedMs(callId),
                    name: err?.name ?? null,
                    error: err?.message ?? String(err)
                });
                await this.restorePreviouslyActiveCall(heldCallIds);
                return false;
            } finally {
                window.clearTimeout(slowAnswerTimer);
                this.answerInFlight.delete(callId);
                this.answerStartedAt.delete(callId);
            }
        });

        this.answerInFlight.set(callId, answerPromise);
        return answerPromise;
    }

    async decline(callId: string): Promise<boolean> {
        const inv = this.incomingCalls.get(callId);
        if (!inv) {
            console.warn("[ETS] decline: no incoming call for id", callId);
            return false;
        }

        try {
            console.info("[ETS] decline() [real]", { callId });

            await (inv as any).reject?.({ statusCode: 486 } as any);

            // Do not emit "ended" here.
            // The Invitation stateChange handler will emit "ended" when the session reaches Terminated.
            return true;
        } catch (err: any) {
            console.error("[ETS] decline() failed", { callId, err: err?.message ?? String(err) });
            return false;
        }
    }

    async hangup(callId: string): Promise<boolean> {
        // 1) Outbound call?
        const inviter = this.calls.get(callId);
        if (inviter) {
            return await this.hangupSession(callId, inviter, /*isIncoming*/ false);
        }

        // 2) Incoming call?
        const inv = this.incomingCalls.get(callId);
        if (inv) {
            return await this.hangupSession(callId, inv, /*isIncoming*/ true);
        }

        console.warn("[ETS] hangup: no call for id", callId);
        return false;
    }

    private async hangupSession(callId: string, session: any, isIncoming: boolean): Promise<boolean> {
        try {
            const state = session?.state;
            console.info("[ETS] hangup() [real]", { callId, state: String(state), isIncoming });
            this.stopLocalRingback(callId);
            // If already terminated, just cleanup
            if (state === SessionState.Terminated) {
                if (isIncoming) {
                    this.incomingCalls.delete(callId);
                    this.answerInFlight.delete(callId);
                }
                else this.calls.delete(callId);
                return true;
            }

            // If established, BYE
            if (state === SessionState.Established) {
                await session.bye?.();
                return true;
            }

            // If still establishing, cancel/reject depending on direction
            if (state === SessionState.Establishing) {
                // outbound: cancel, inbound: reject (if not yet accepted)
                if (isIncoming) {
                    await session.reject?.({ statusCode: 486 } as any);
                } else {
                    await session.cancel?.();
                }
                return true;
            }

            // Fallback attempts
            try { await session.bye?.(); return true; } catch { /* ignore */ }
            try {
                if (isIncoming) await session.reject?.({ statusCode: 486 } as any);
                else await session.cancel?.();
                return true;
            } catch { /* ignore */ }

            session.dispose?.();
            return true;

        } catch (err: any) {
            console.error("[ETS] hangup() failed", { callId, err: err?.message ?? String(err) });
            return false;
        }
    }

    async blindTransfer(callId: string, targetUri: string): Promise<boolean> {
        const session = this.getCallSession(callId);
        if (!session) {
            console.warn("[ETS] blindTransfer: no call for id", callId);
            return false;
        }

        const target = this.buildTargetUri(targetUri);
        if (!target) {
            console.warn("[ETS] blindTransfer: invalid target", { callId, targetUri });
            return false;
        }

        try {
            const state = session.state;
            if (state !== SessionState.Established) {
                console.warn("[ETS] blindTransfer: call not established", { callId, state: String(state) });
                return false;
            }

            console.info("[ETS][transfer][sip] blindTransfer REFER sending", {
                callId,
                targetUri,
                target: target.toString(),
                state: String(state),
            });
            const referOutcome = this.createReferOutcome("blind", callId, targetUri);
            await session.refer(target as any, referOutcome.options);
            console.info("[ETS][transfer][sip] blindTransfer REFER sent", { callId, targetUri });
            const transferSucceeded = await referOutcome.wait();
            if (!transferSucceeded) {
                console.warn("[ETS][transfer][sip] blindTransfer failed after REFER", { callId, targetUri });
                await this.reconcileCallAfterFailedRefer(callId, session, "blind", targetUri, true);
                return false;
            }

            this.emitCallUpdated(callId, "ended", undefined, false);
            this.removeCallSession(callId);
            return true;
        } catch (err: any) {
            console.error("[ETS] blindTransfer() failed", { callId, targetUri, err: err?.message ?? String(err) });
            return false;
        }
    }

    async startAttendedTransfer(sourceCallId: string, targetUri: string): Promise<{ sourceCallId: string; consultCallId: string } | null> {
        const source = this.getCallSession(sourceCallId);
        if (!source) {
            console.warn("[ETS] startAttendedTransfer: no source call for id", sourceCallId);
            return null;
        }

        if (source.state !== SessionState.Established) {
            console.warn("[ETS] startAttendedTransfer: source call not established", {
                sourceCallId,
                state: String(source.state),
            });
            return null;
        }

        const held = await this.hold(sourceCallId);
        if (!held) return null;

        try {
            const consultCallId = await this.dial(targetUri);
            console.info("[ETS][transfer][sip] startAttendedTransfer consult created", { sourceCallId, targetUri, consultCallId });
            return { sourceCallId, consultCallId };
        } catch (err: any) {
            console.error("[ETS] startAttendedTransfer() failed", {
                sourceCallId,
                targetUri,
                err: err?.message ?? String(err),
            });
            await this.resume(sourceCallId);
            return null;
        }
    }

    async completeAttendedTransfer(sourceCallId: string, consultCallId: string): Promise<boolean> {
        const source = this.getCallSession(sourceCallId);
        const consult = this.getCallSession(consultCallId);
        if (!source || !consult) {
            console.warn("[ETS] completeAttendedTransfer: missing call", {
                sourceCallId,
                consultCallId,
                hasSource: !!source,
                hasConsult: !!consult,
            });
            return false;
        }

        if (source.state !== SessionState.Established || consult.state !== SessionState.Established) {
            console.warn("[ETS] completeAttendedTransfer: both calls must be established", {
                sourceCallId,
                sourceState: String(source.state),
                consultCallId,
                consultState: String(consult.state),
            });
            return false;
        }

        try {
            console.info("[ETS][transfer][sip] completeAttendedTransfer REFER/Replaces sending", {
                sourceCallId,
                consultCallId,
                sourceState: String(source.state),
                consultState: String(consult.state),
            });
            const referOutcome = this.createReferOutcome("attended-complete", sourceCallId, consultCallId);
            await source.refer(consult as any, referOutcome.options);
            console.info("[ETS][transfer][sip] completeAttendedTransfer REFER/Replaces sent", { sourceCallId, consultCallId });
            const transferSucceeded = await referOutcome.wait();
            if (!transferSucceeded) {
                console.warn("[ETS][transfer][sip] completeAttendedTransfer failed after REFER/Replaces", { sourceCallId, consultCallId });
                await this.reconcileCallAfterFailedRefer(sourceCallId, source, "attended-complete", consultCallId, false);
                return false;
            }

            try { await this.hangupSession(consultCallId, consult, this.incomingCalls.has(consultCallId)); } catch { /* ignore */ }
            this.emitCallUpdated(consultCallId, "ended", undefined, false);
            this.emitCallUpdated(sourceCallId, "ended", undefined, false);
            this.removeCallSession(consultCallId);
            this.removeCallSession(sourceCallId);
            return true;
        } catch (err: any) {
            console.error("[ETS] completeAttendedTransfer() failed", {
                sourceCallId,
                consultCallId,
                err: err?.message ?? String(err),
            });
            return false;
        }
    }

    async cancelAttendedTransfer(sourceCallId: string, consultCallId: string): Promise<boolean> {
        const existing = this.attendedCancelInFlight.get(consultCallId);
        if (existing) {
            console.info("[ETS][transfer][sip] joining attended cancellation already in progress", {
                sourceCallId,
                consultCallId
            });
            return existing;
        }

        const source = this.getCallSession(sourceCallId);
        const consult = this.getCallSession(consultCallId);
        if (!consult) {
            console.warn("[ETS] cancelAttendedTransfer: missing call", {
                sourceCallId,
                consultCallId,
                hasSource: !!source,
                hasConsult: !!consult,
            });
            return false;
        }

        const cancellation = (async () => {
            this.terminatingCallIds.add(consultCallId);
            try {
                console.info("[ETS][transfer][sip] cancelAttendedTransfer requested", {
                    sourceCallId,
                    consultCallId,
                    sourceState: String(source?.state ?? "missing"),
                    consultState: String(consult.state),
                });
                const terminated = await this.hangupSession(
                    consultCallId,
                    consult,
                    this.incomingCalls.has(consultCallId));
                if (!terminated) {
                    this.terminatingCallIds.delete(consultCallId);
                    console.warn("[ETS][transfer][sip] consultation termination failed; source remains held", {
                        sourceCallId,
                        consultCallId
                    });
                    return false;
                }

                const definitivelyEnded = await this.waitForSessionTermination(
                    consult,
                    5000,
                    "attended-cancel");
                if (!definitivelyEnded) {
                    this.terminatingCallIds.delete(consultCallId);
                    console.warn("[ETS][transfer][sip] consultation termination was not acknowledged; source remains held", {
                        sourceCallId,
                        consultCallId,
                        consultState: String(consult.state)
                    });
                    return false;
                }

                if (this.callMeta.has(consultCallId)) {
                    this.emitCallUpdated(consultCallId, "ended", undefined, false);
                }
                this.removeCallSession(consultCallId);
                this.terminatingCallIds.delete(consultCallId);

                const currentSource = this.getCallSession(sourceCallId);
                if (!currentSource || currentSource.state === SessionState.Terminated) {
                    console.info("[ETS][transfer][sip] consultation ended; source call already ended so no resume is required", {
                        sourceCallId,
                        consultCallId
                    });
                    return true;
                }

                console.info("[ETS][transfer][sip] source resume requested after consultation termination", {
                    sourceCallId,
                    consultCallId,
                    sourceState: String(currentSource.state)
                });
                const resumed = await this.resume(sourceCallId);
                console.info("[ETS][transfer][sip] source resume result after consultation termination", {
                    sourceCallId,
                    consultCallId,
                    resumed
                });
                return resumed;
            } catch (err: any) {
                this.terminatingCallIds.delete(consultCallId);
                console.error("[ETS] cancelAttendedTransfer() failed", {
                    sourceCallId,
                    consultCallId,
                    err: err?.message ?? String(err),
                });
                return false;
            } finally {
                this.attendedCancelInFlight.delete(consultCallId);
            }
        })();

        this.attendedCancelInFlight.set(consultCallId, cancellation);
        return cancellation;
    }


    async hold(callId: string): Promise<boolean> {
        const session = this.calls.get(callId) ?? this.incomingCalls.get(callId);
        if (!session) {
            console.warn("[ETS] hold: no call for id", callId);
            return false;
        }

        try {
            const state = session.state;
            if (state !== SessionState.Established) {
                console.warn("[ETS] hold: call not established", { callId, state: String(state) });
                return false;
            }

            console.info("[ETS] hold() [real]", { callId });

            await session.invite({
                sessionDescriptionHandlerOptions: { hold: true }
            } as any);

            this.emitCallUpdated(callId, "held", undefined, false);
            return true;

        } catch (err: any) {
            console.error("[ETS] hold() failed", { callId, err: err?.message ?? String(err) });
            return false;
        }
    }

    async resume(callId: string): Promise<boolean> {
        return this.runActiveCallTransition(async () => {
            const heldCallIds = await this.holdOtherActiveCalls(callId);
            if (heldCallIds === null) {
                return false;
            }

            const resumed = await this.resumeSession(callId);
            if (!resumed) {
                await this.restorePreviouslyActiveCall(heldCallIds);
            }
            return resumed;
        });
    }

    private async resumeSession(callId: string): Promise<boolean> {
        const session = this.calls.get(callId) ?? this.incomingCalls.get(callId);
        if (!session) {
            console.warn("[ETS] resume: no call for id", callId);
            return false;
        }

        try {
            const state = session.state;
            if (state !== SessionState.Established) {
                console.warn("[ETS] resume: call not established", { callId, state: String(state) });
                return false;
            }

            console.info("[ETS] resume() [real]", { callId });

            await session.invite({
                sessionDescriptionHandlerOptions: { hold: false }
            } as any);

            // A consultation leg may have replaced the shared audio element's MediaStream.
            // Rebind it to the resumed source session after the unhold re-INVITE completes.
            this.attachRemoteAudio(session, callId, "resume");
            this.emitCallUpdated(callId, "active", undefined, true);
            return true;

        } catch (err: any) {
            console.error("[ETS] resume() failed", { callId, err: err?.message ?? String(err) });
            return false;
        }
    }

    async makeActive(callId: string): Promise<boolean> {
        return this.runActiveCallTransition(async () => {
            const hasCall =
                this.calls.has(callId) || this.incomingCalls.has(callId) || this.callMeta.has(callId);

            if (!hasCall) {
                console.warn("[ETS] makeActive: no call for id", callId);
                return false;
            }

            const otherActiveCallIds = this.otherActiveCallIds(callId);
            const meta = this.callMeta.get(callId);
            if (this.activeCallId === callId && otherActiveCallIds.length === 0 && meta?.state === "active") {
                console.info("[ETS] makeActive() ignored; already exclusively active", { callId });
                return true;
            }

            const previousActiveCallId = this.activeCallId;
            const heldCallIds = await this.holdOtherActiveCalls(callId);
            if (heldCallIds === null) {
                return false;
            }

            console.info("[ETS] makeActive() [real]", { callId, previousActiveCallId });

            if (meta?.state === "held" && !await this.resumeSession(callId)) {
                await this.restorePreviouslyActiveCall(heldCallIds);
                return false;
            }

            this.activeCallId = callId;

            // Re-emit callUpdated for all known calls so every UI surface receives
            // one authoritative active-call selection.
            for (const [id, currentMeta] of this.callMeta.entries()) {
                if (currentMeta.state === "ended") continue;
                const active = id === callId;
                emit("callUpdated", this.events, {
                    callId: id,
                    remoteUri: currentMeta.remoteUri ?? null,
                    state: currentMeta.state,
                    isActive: active,
                } as any);
            }

            return true;
        });
    }


    /**
     * Send RFC2833 DTMF (WebRTC "telephone-event") for an established call.
     */
    async sendDtmf(callId: string, tones: string, durationMs = 200, gapMs = 70): Promise<boolean> {
        const inviter = this.calls.get(callId);
        if (!inviter) {
            console.warn("[ETS] sendDtmf: no call for id", callId);
            return false;
        }

        const state = (inviter as any).state;
        if (state && String(state) !== "Established") {
            console.warn("[ETS] sendDtmf: call not established", { callId, state: String(state) });
            return false;
        }

        const sdh: any = (inviter as any).sessionDescriptionHandler;
        const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
        if (!pc) {
            console.warn("[ETS] sendDtmf: peerConnection not ready", callId);
            return false;
        }

        const sender = pc.getSenders().find((s) => s.track?.kind === "audio" && (s as any).dtmf);
        const dtmf: RTCDTMFSender | undefined = (sender as any)?.dtmf;
        if (!dtmf) {
            console.warn("[ETS] sendDtmf: no RTCDTMFSender available", { callId });
            return false;
        }

        if ((dtmf as any).canInsertDTMF === false) {
            console.warn("[ETS] sendDtmf: canInsertDTMF=false", { callId });
            return false;
        }

        dtmf.insertDTMF(tones, durationMs, gapMs);
        console.info("[ETS] sendDtmf queued", { callId, tones, durationMs, gapMs });
        return true;
    }

    // --- Registration surface ---

    async register(): Promise<void> {
        if (this.registrationInFlight) return this.registrationInFlight;
        const ua = this.ua;
        if (!ua || !ua.isConnected()) {
            this.emitRegistration("unregistered", "Transport not connected");
            throw new Error("Transport not connected");
        }
        const epoch = this.transportEpoch;
        if (this.reg && this.regState === RegistererState.Terminated) {
            this.reg = undefined;
            this.regState = undefined;
        }
        if (!this.reg) {
            const registerer = new Registerer(ua);
            this.reg = registerer;
            this.regState = RegistererState.Initial;
            registerer.stateChange.addListener((state: RegistererState) => {
                if (this.ua !== ua || this.reg !== registerer) return;
                this.regState = state;
                if (state === RegistererState.Unregistered && this.unregisterRequested) {
                    this.unregisterRequested = false;
                    this.emitRegistration("unregistered");
                    return;
                }
                // A response belonging to the lost transport must not restore
                // the UI's registered state after reconnecting the same UA.
                if (this.registrationTransportEpoch !== this.transportEpoch) return;
                if (state === RegistererState.Unregistered) {
                    this.emitRegistration("unregistered", this.lastRegisterRejectMessage,
                        this.lastRegisterRejectCode, this.lastRegisterRejectReason);
                } else if (state === RegistererState.Terminated) {
                    this.emitRegistration("unregistered", "Registration terminated");
                }
            });
        }
        const registerer = this.reg;
        const current = () => this.ua === ua && this.reg === registerer &&
            this.transportEpoch === epoch && ua.isConnected();
        this.lastRegisterRejectMessage = undefined;
        this.lastRegisterRejectCode = undefined;
        this.lastRegisterRejectReason = undefined;
        this.unregisterRequested = false;
        this.emitRegistration("registering");

        const pending = new Promise<void>((resolve, reject) => {
            let settled = false;
            let retryTimer: ReturnType<typeof setTimeout> | undefined;
            const finish = (error?: Error) => {
                if (settled) return;
                settled = true;
                clearTimeout(deadline);
                if (retryTimer !== undefined) clearTimeout(retryTimer);
                if (this.cancelRegistrationAttempt === cancel) this.cancelRegistrationAttempt = undefined;
                if (error) reject(error); else resolve();
            };
            const cancel = () => finish(new Error("Registration superseded or transport disconnected"));
            const deadline = setTimeout(() => finish(new Error("Registration timed out")), 35_000);
            this.cancelRegistrationAttempt = cancel;
            const submit = async (): Promise<void> => {
                if (settled) return;
                if (!current()) { cancel(); return; }
                const previousEpoch = this.registrationTransportEpoch;
                this.registrationTransportEpoch = epoch;
                try {
                    await registerer.register({ requestDelegate: {
                        onAccept: () => {
                            if (settled) return;
                            if (!current()) { cancel(); return; }
                            if (registerer.state !== RegistererState.Registered) {
                                finish(new Error("Registration was not accepted")); return;
                            }
                            // A re-REGISTER of an already Registered instance
                            // does not emit another SIP.js state transition.
                            this.emitRegistration("registered");
                            void this.subscribeVoicemail();
                            finish();
                        },
                        onReject: (response: any) => {
                            if (settled) return;
                            if (!current()) { cancel(); return; }
                            const code = response?.message?.statusCode ?? response?.statusCode;
                            const reason = response?.message?.reasonPhrase ?? response?.reasonPhrase;
                            this.lastRegisterRejectCode = typeof code === "number" ? code : undefined;
                            this.lastRegisterRejectReason = typeof reason === "string" ? reason : undefined;
                            this.lastRegisterRejectMessage = this.lastRegisterRejectCode != null
                                ? `Registration rejected (${this.lastRegisterRejectCode}).` : "Registration rejected.";
                            this.emitRegistration("unregistered", this.lastRegisterRejectMessage,
                                this.lastRegisterRejectCode, this.lastRegisterRejectReason);
                            finish(new Error(this.lastRegisterRejectMessage));
                        }
                    }});
                } catch (error) {
                    if (settled) return;
                    if (error instanceof RequestPendingError && current()) {
                        // The previous transport's REGISTER transaction can
                        // remain pending until its SIP timeout. Reuse this
                        // Registerer's Call-ID/CSeq; disposing it can later
                        // send an UNREGISTER which removes the new contact.
                        this.registrationTransportEpoch = previousEpoch;
                        retryTimer = setTimeout(() => { void submit(); }, 250);
                        return;
                    }
                    finish(error instanceof Error ? error : new Error("Registration failed"));
                }
            };
            void submit();
        });
        this.registrationInFlight = pending;
        try { await pending; }
        catch (error) {
            if (current() && this.registrationTransportEpoch === epoch)
                this.emitRegistration("unregistered", "Registration failed");
            throw error;
        } finally {
            if (this.registrationInFlight === pending) this.registrationInFlight = undefined;
        }
    }

    async unregister(): Promise<void> {
        console.info("[ETS] unregister() [real]");

        this.cancelRegistrationAttempt?.();
        this.registrationInFlight = undefined;
        this.registrationTransportEpoch = -1;
        this.unregisterRequested = true;

        this.lastRegisterRejectMessage = undefined;
        this.lastRegisterRejectCode = undefined;
        this.lastRegisterRejectReason = undefined;

        if (this.reg && this.regState === RegistererState.Terminated) {
            this.reg = undefined;
            this.regState = RegistererState.Initial;
        }

        if (!this.ua || !this.reg) {
            this.emitRegistration("unregistered");
            return;
        }

        const ua = this.ua;
        const registerer = this.reg;
        try {
            await registerer.unregister();
        } catch (err: any) {
            if (this.ua !== ua || this.reg !== registerer) throw err;
            console.error("[ETS] reg.unregister() failed", err);

            emit("registrationChanged", this.events, {
                state: "unregistered",
                message: (err?.message ?? String(err))?.trim(),
            } as any);

            throw err;
        }
    }

    private wireIncoming(ua: UserAgent) {
        ua.delegate = {
            onInvite: (inv: Invitation) => {
                if (this.ua !== ua) return;
                const callId =
                    (inv.request as any)?.callId ??
                    (inv.request as any)?.callIdHeader ??
                    this.newCallId();

                const remoteUri = inv.request.from?.uri?.toString();

                this.markAnswerTiming("incoming INVITE received", callId, inv, {
                    documentVisibility: document.visibilityState
                });

                console.warn("[ETS] Incoming INVITE (wired)", {
                    callId,
                    from: remoteUri,
                    to: inv.request.to?.uri?.toString(),
                });

                // Store so Answer/Decline can find it
                this.incomingCalls.set(callId, inv);

                // Listen immediately so CANCEL / termination before answer is reflected in UI
                (inv as any).stateChange?.addListener?.((state: any) => {
                    this.markAnswerTiming("SIP session state changed", callId, inv, {
                        state: String(state),
                        answerElapsedMs: this.answerElapsedMs(callId),
                        documentVisibility: document.visibilityState
                    });
                    console.info("[ETS][answer-timing] incoming state changed", {
                        callId,
                        state: String(state),
                        answerElapsedMs: this.answerElapsedMs(callId)
                    });

                    if (state === SessionState.Establishing) {
                        this.logSessionMediaDiagnostics(inv, callId, "incoming-establishing");
                        this.markAnswerTiming("call state becomes establishing", callId, inv, {
                            answerElapsedMs: this.answerElapsedMs(callId)
                        });
                        this.emitCallUpdated(callId, "establishing", remoteUri, false);
                        return;
                    }

                    if (state === SessionState.Established) {
                        this.stopLocalRingback(callId, "incoming-established");
                        try {
                            this.attachRemoteAudio(inv, callId, "incoming-established");
                        } catch (err) {
                            console.warn("[ETS][early-media] attachRemoteAudio(incoming) failed", { callId, err });
                        }
                        this.markAnswerTiming("call state becomes active", callId, inv, {
                            answerElapsedMs: this.answerElapsedMs(callId)
                        });
                        this.emitCallUpdated(callId, "active", remoteUri, true);
                        return;
                    }

                    if (state === SessionState.Terminated) {
                        this.stopLocalRingback(callId, "incoming-terminated");
                        this.incomingCalls.delete(callId);
                        this.answerInFlight.delete(callId);
                        this.emitCallUpdated(callId, "ended", remoteUri, false);
                        return;
                    }
                });

                // Emit the state the UI expects
                this.emitCallUpdated(callId, "incoming", remoteUri, false);
            },
            onNotify: (notification: any) => this.handleNotify(notification),
        };

        console.info("[ETS] UA delegate wired (onInvite, onNotify)");
    }

    private handleNotify(notification: any): void {
        try {
            const request =
                notification?.request?.message ??
                notification?.request ??
                notification?.message ??
                {};
            const getHeader = (name: string) =>
                request?.getHeader?.(name) ??
                request?.getHeader?.(name.toLowerCase()) ??
                null;
            const body =
                request?.body ??
                notification?.request?.body ??
                notification?.message?.body ??
                "";

            emit("notifyReceived", this.events, {
                eventHeader: getHeader("Event"),
                contentType: getHeader("Content-Type"),
                subscriptionState: getHeader("Subscription-State"),
                body: String(body || "")
            });
        } catch (err) {
            console.warn("[ETS] SIP NOTIFY extraction failed", {
                error: err instanceof Error ? err.message : String(err)
            });
        } finally {
            try { notification.accept?.(); } catch { /* SIP processing must continue */ }
        }
    }

    private async subscribeVoicemail(): Promise<void> {
        if (!this.ua || this.voicemailSubscriber) {
            return;
        }

        const target = UserAgent.makeURI(String(this.lastCfg?.sipUri || "").trim());
        if (!target) {
            console.warn("[ETS] Voicemail subscription skipped; SIP URI was invalid");
            return;
        }

        const subscriber = new Subscriber(this.ua, target, "message-summary", {
            expires: 300
        } as any);
        this.voicemailSubscriber = subscriber;
        subscriber.delegate = {
            onNotify: (notification: any) => this.handleNotify(notification)
        };
        subscriber.stateChange.addListener((state: any) => {
            console.info("[ETS] Voicemail subscription state", { state: String(state) });
            if (state === SubscriptionState.Terminated && this.voicemailSubscriber === subscriber) {
                this.voicemailSubscriber = undefined;
            }
        });

        try {
            await subscriber.subscribe({
                requestOptions: {
                    extraHeaders: ["Accept: application/simple-message-summary"]
                }
            } as any);
            console.info("[ETS] Voicemail message-summary subscription requested");
        } catch (err) {
            if (this.voicemailSubscriber === subscriber) {
                this.voicemailSubscriber = undefined;
            }
            console.warn("[ETS] Voicemail message-summary subscription failed", {
                error: err instanceof Error ? err.message : String(err)
            });
        }
    }

    private async unsubscribeVoicemail(): Promise<void> {
        const subscriber = this.voicemailSubscriber;
        this.voicemailSubscriber = undefined;
        if (!subscriber) {
            return;
        }
        try { await subscriber.unsubscribe?.(); } catch { /* ignore */ }
    }

    private wireTransport(ua: UserAgent) {
        (ua.transport as any).stateChange.addListener((state: any) => {
            if (this.ua !== ua) return;
            const s = String(state).toLowerCase();

            if (s.includes("disconnect")) {
                if (this.lastTransportState !== "disconnected") {
                    ++this.transportEpoch;
                    this.cancelRegistrationAttempt?.();
                }
                this.emitTransport("disconnected");
                this.emitRegistration("unregistered");
                return;
            }

            if (s.includes("connecting")) {
                this.emitTransport("connecting");
                return;
            }

            if (s.includes("connected")) {
                this.emitTransport("connected");
                return;
            }

            console.info("[ETS] SIP.js transport state:", state);
        });
    }

    private isSameCfg(a?: PbxConfig, b?: PbxConfig): boolean {
        if (!a || !b) return false;
        return a.wssUrl === b.wssUrl && a.sipUri === b.sipUri && a.authUser === b.authUser && a.password === b.password;
    }

    private emitTransport(state: "disconnected" | "connecting" | "connected", error?: string) {
        if (this.lastTransportState === state) return;
        this.lastTransportState = state;
        emit("transportChanged", this.events, error ? ({ state, error } as any) : ({ state } as any));
    }

    private emitRegistration(
        state: "unregistered" | "registering" | "registered",
        message?: string,
        code?: number,
        reason?: string
    ) {
        const msg = message?.trim();

        if (this.lastRegUiState === state && msg === undefined && code === undefined && reason === undefined) return;

        this.lastRegUiState = state;

        const payload: any = { state };
        if (msg != null && msg.length > 0) payload.message = msg;
        if (code != null) payload.code = code;
        if (reason != null && reason.trim().length > 0) payload.reason = reason.trim();

        emit("registrationChanged", this.events, payload);
    }

    private newCallId(): string {
        try {
            // @ts-ignore
            if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
                // @ts-ignore
                return crypto.randomUUID();
            }
        } catch { /* ignore */ }

        return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
    }

    private emitCallUpdated(
        callId: string,
        state: "incoming" | "establishing" | "active" | "held" | "ended",
        remoteUri?: string,
        isActive?: boolean,
        failureCode?: number,
        failureReason?: string,
        wasAnswered?: boolean
    ) {
        const previous = this.callMeta.get(callId);
        const nextRemoteUri = remoteUri !== undefined ? remoteUri : previous?.remoteUri ?? null;
        const session = this.incomingCalls.get(callId) ?? this.calls.get(callId);
        const remoteDisplayName = remoteCallerDisplayName(session?.remoteIdentity, session?.assertedIdentity) ?? previous?.remoteDisplayName;

        // Cache last-known values
        this.callMeta.set(callId, { state, remoteUri: nextRemoteUri, remoteDisplayName });

        // If caller didn't specify isActive, preserve current active selection
        const active = typeof isActive === "boolean" ? isActive : this.activeCallId === callId;
        if (active) this.activeCallId = callId;
        else if (state === "held" && this.activeCallId === callId) this.activeCallId = undefined;

        console.log("[ETS] emitCallUpdated", {
            callId,
            state,
            remoteUri: nextRemoteUri,
            isActive: active,
            failureCode,
            failureReason,
            wasAnswered,
            hasEvents: !!this.events,
        });

        emit("callUpdated", this.events, {
            callId,
            remoteUri: nextRemoteUri,
            state,
            isActive: !!active,
            failureCode,
            failureReason,
            wasAnswered,
            remoteDisplayName,
        } as any);

        // Clean up ended calls
        if (state === "ended") {
            this.callMeta.delete(callId);
            if (this.activeCallId === callId) this.activeCallId = undefined;
            this.outboundEarlyMediaCalls.delete(callId);
        }
    }


    private buildTargetUri(target: string) {
        const t = (target ?? "").trim();
        if (!t) return undefined;

        if (t.toLowerCase().startsWith("sip:")) {
            return UserAgent.makeURI(t) ?? undefined;
        }

        const domain = this.getDomainFromSipUri(this.lastCfg?.sipUri);
        if (!domain) return undefined;

        return UserAgent.makeURI(`sip:${t}@${domain}`) ?? undefined;
    }

    private getCallSession(callId: string): any {
        return this.calls.get(callId) ?? this.incomingCalls.get(callId);
    }

    private removeCallSession(callId: string) {
        this.calls.delete(callId);
        this.incomingCalls.delete(callId);
        this.answerInFlight.delete(callId);
        if (this.activeCallId === callId) this.activeCallId = undefined;
    }

    private async reconcileCallAfterFailedRefer(callId: string, session: any, mode: string, target: string, endIfStillEstablished: boolean): Promise<void> {
        const ended = await this.waitForSessionTermination(session, 1500, "failed-refer");
        const state = String(session?.state ?? "unknown");

        if (!ended && session?.state !== SessionState.Terminated) {
            if (!endIfStillEstablished) {
                console.info("[ETS][transfer][sip] REFER failed but original session still appears established", {
                    mode,
                    callId,
                    target,
                    state
                });
                return;
            }

            console.warn("[ETS][transfer][sip] REFER failed; ending UI call to match blind-transfer device state", {
                mode,
                callId,
                target,
                state
            });
        }
        else {
            console.warn("[ETS][transfer][sip] REFER failed and original session terminated; ending UI call", {
                mode,
                callId,
                target,
                state
            });
        }

        this.emitCallUpdated(callId, "ended", undefined, false);
        this.removeCallSession(callId);
    }

    private waitForSessionTermination(
        session: any,
        timeoutMs: number,
        context = "session-termination"): Promise<boolean> {
        if (session?.state === SessionState.Terminated) {
            return Promise.resolve(true);
        }

        return new Promise(resolve => {
            let settled = false;
            let timer = 0;

            const finish = (value: boolean) => {
                if (settled) return;
                settled = true;
                if (timer) window.clearTimeout(timer);
                try { session?.stateChange?.removeListener?.(listener); } catch { /* ignore */ }
                resolve(value);
            };

            const listener = (state: any) => {
                console.info("[ETS][transfer][sip] session state while awaiting termination", {
                    context,
                    state: String(state)
                });
                if (state === SessionState.Terminated) {
                    finish(true);
                }
            };

            try { session?.stateChange?.addListener?.(listener); } catch { /* ignore */ }

            timer = window.setTimeout(() => {
                finish(session?.state === SessionState.Terminated);
            }, timeoutMs);
        });
    }

    private createReferOutcome(mode: string, callId: string, target: string) {
        let settled = false;
        let settle: (ok: boolean) => void = () => { };
        const result = new Promise<boolean>(resolve => {
            settle = ok => {
                if (settled) return;
                settled = true;
                resolve(ok);
            };
        });

        const timeout = window.setTimeout(() => {
            console.warn("[ETS][transfer][sip] REFER final NOTIFY timed out", { mode, callId, target });
            settle(false);
        }, 15000);

        return {
            wait: async () => {
                const ok = await result;
                window.clearTimeout(timeout);
                return ok;
            },
            options: {
            onNotify: (notification: any) => {
                const body =
                    notification?.request?.body ??
                    notification?.message?.body ??
                    "";

                const contentType =
                    notification?.request?.message?.getHeader?.("Content-Type") ??
                    notification?.request?.getHeader?.("Content-Type") ??
                    notification?.message?.getHeader?.("Content-Type") ??
                    null;

                console.info("[ETS][transfer][sip] REFER notify", {
                    mode,
                    callId,
                    target,
                    contentType,
                    body: String(body || "")
                });

                const statusCode = this.parseSipFragStatusCode(String(body || ""));
                if (statusCode >= 200 && statusCode < 300) {
                    console.info("[ETS][transfer][sip] REFER final NOTIFY succeeded", { mode, callId, target, statusCode });
                    settle(true);
                } else if (statusCode >= 300) {
                    console.warn("[ETS][transfer][sip] REFER final NOTIFY failed", { mode, callId, target, statusCode, body: String(body || "") });
                    settle(false);
                }

                try { notification.accept?.(); } catch { /* ignore */ }
            },
            requestDelegate: {
                onAccept: (response: any) => {
                    console.info("[ETS][transfer][sip] REFER accepted", {
                        mode,
                        callId,
                        target,
                        code: response?.message?.statusCode ?? response?.statusCode,
                        reason: response?.message?.reasonPhrase ?? response?.reasonPhrase
                    });
                },
                onReject: (response: any) => {
                    console.warn("[ETS][transfer][sip] REFER rejected", {
                        mode,
                        callId,
                        target,
                        code: response?.message?.statusCode ?? response?.statusCode,
                        reason: response?.message?.reasonPhrase ?? response?.reasonPhrase
                    });
                }
            }
            } as any
        };
    }

    private parseSipFragStatusCode(body: string): number {
        const match = String(body || "").match(/SIP\/2\.0\s+(\d{3})/i);
        if (!match) return 0;

        const parsed = Number.parseInt(match[1], 10);
        return Number.isFinite(parsed) ? parsed : 0;
    }

    private getDomainFromSipUri(sipUri?: string): string | undefined {
        if (!sipUri) return undefined;
        const at = sipUri.indexOf("@");
        if (at < 0) return undefined;

        const host = sipUri.slice(at + 1).trim();
        if (!host) return undefined;

        return host.split(";")[0].trim();
    }

    private getSipMessageBody(response: any): string {
        const body =
            response?.message?.body ??
            response?.body ??
            response?.incomingResponse?.message?.body ??
            response?.incomingResponse?.body ??
            "";

        return String(body || "");
    }

    private getSipMessageHeader(response: any, name: string): string | null {
        try {
            return (
                response?.message?.getHeader?.(name) ??
                response?.getHeader?.(name) ??
                response?.incomingResponse?.message?.getHeader?.(name) ??
                null
            );
        } catch {
            return null;
        }
    }

    private looksLikeSdp(body: string, contentType?: string | null): boolean {
        const type = String(contentType || "").toLowerCase();
        const text = String(body || "");

        return type.includes("application/sdp") ||
            (text.includes("v=0") && text.includes("m=audio"));
    }

    private summariseSdp(body: string) {
        const lines = String(body || "")
            .split(/\r?\n/)
            .map(x => x.trim())
            .filter(Boolean);

        const audioLine = lines.find(x => x.startsWith("m=audio")) ?? null;
        const connectionLine = lines.find(x => x.startsWith("c=")) ?? null;
        const direction = lines.find(x =>
            x === "a=sendrecv" ||
            x === "a=sendonly" ||
            x === "a=recvonly" ||
            x === "a=inactive") ?? null;
        const iceUfrag = lines.find(x => x.startsWith("a=ice-ufrag:")) ? true : false;
        const fingerprint = lines.find(x => x.startsWith("a=fingerprint:")) ? true : false;
        const codecs = lines
            .filter(x => x.startsWith("a=rtpmap:"))
            .map(x => x.slice("a=rtpmap:".length))
            .slice(0, 12);

        return {
            lineCount: lines.length,
            audioLine,
            connectionLine,
            direction,
            hasIceUfrag: iceUfrag,
            hasFingerprint: fingerprint,
            codecs
        };
    }

    private describePeerConnection(pc?: RTCPeerConnection | null) {
        if (!pc) return null;

        return {
            signalingState: pc.signalingState,
            iceGatheringState: pc.iceGatheringState,
            iceConnectionState: pc.iceConnectionState,
            connectionState: pc.connectionState,
            localDescription: pc.localDescription
                ? {
                    type: pc.localDescription.type,
                    hasSdp: !!pc.localDescription.sdp,
                    sdp: this.summariseSdp(pc.localDescription.sdp || "")
                }
                : null,
            remoteDescription: pc.remoteDescription
                ? {
                    type: pc.remoteDescription.type,
                    hasSdp: !!pc.remoteDescription.sdp,
                    sdp: this.summariseSdp(pc.remoteDescription.sdp || "")
                }
                : null,
            senders: pc.getSenders().map((s: RTCRtpSender) => ({
                trackKind: s.track?.kind ?? null,
                trackId: s.track?.id ?? null,
                trackReadyState: s.track?.readyState ?? null,
                hasDtmf: !!(s as any).dtmf
            })),
            receivers: pc.getReceivers().map((r: RTCRtpReceiver) => ({
                trackKind: r.track?.kind ?? null,
                trackId: r.track?.id ?? null,
                trackEnabled: r.track?.enabled ?? null,
                trackMuted: r.track?.muted ?? null,
                trackReadyState: r.track?.readyState ?? null
            })),
            transceivers: pc.getTransceivers?.().map((t: RTCRtpTransceiver) => ({
                mid: t.mid,
                direction: t.direction,
                currentDirection: t.currentDirection,
                receiverTrackKind: t.receiver?.track?.kind ?? null,
                receiverTrackReadyState: t.receiver?.track?.readyState ?? null
            })) ?? []
        };
    }

    private wirePeerConnectionDiagnostics(pc: RTCPeerConnection, callId: string, context: string) {
        if (this.pcDiagnosticsWired.has(pc)) return;
        this.pcDiagnosticsWired.add(pc);

        console.info("[ETS][early-media] peerConnection diagnostics wired", {
            callId,
            context,
            answerElapsedMs: this.answerElapsedMs(callId),
            pc: this.describePeerConnection(pc)
        });

        pc.addEventListener("icegatheringstatechange", () => {
            this.markAnswerTiming("ICE gathering state changed", callId, undefined, {
                context,
                iceGatheringState: pc.iceGatheringState,
                answerElapsedMs: this.answerElapsedMs(callId)
            });
            console.info("[ETS][answer-timing] ICE gathering state changed", {
                callId,
                context,
                iceGatheringState: pc.iceGatheringState,
                answerElapsedMs: this.answerElapsedMs(callId)
            });
        });

        pc.addEventListener("icecandidate", event => {
            const candidate = event.candidate;
            this.markAnswerTiming("ICE candidate gathered", callId, undefined, {
                context,
                complete: candidate === null,
                type: candidate?.type ?? null,
                protocol: candidate?.protocol ?? null,
                answerElapsedMs: this.answerElapsedMs(callId)
            });
            console.info("[ETS][answer-timing] ICE candidate gathered", {
                callId,
                context,
                complete: candidate === null,
                type: candidate?.type ?? null,
                protocol: candidate?.protocol ?? null,
                relayProtocol: candidate?.relayProtocol ?? null,
                answerElapsedMs: this.answerElapsedMs(callId)
            });
        });

        pc.addEventListener("track", event => {
            this.markAnswerTiming("remote media track received", callId, undefined, {
                context,
                answerElapsedMs: this.answerElapsedMs(callId),
                kind: event.track?.kind,
                muted: event.track?.muted,
                readyState: event.track?.readyState
            });
            console.info("[ETS][early-media] peerConnection track event", {
                callId,
                context,
                track: {
                    kind: event.track?.kind,
                    id: event.track?.id,
                    enabled: event.track?.enabled,
                    muted: event.track?.muted,
                    readyState: event.track?.readyState
                },
                streamCount: event.streams?.length ?? 0,
                streamTracks: (event.streams || []).map(stream => stream.getTracks().map(track => ({
                    kind: track.kind,
                    id: track.id,
                    enabled: track.enabled,
                    muted: track.muted,
                    readyState: track.readyState
                })))
            });

            event.track?.addEventListener("unmute", () => {
                this.markAnswerTiming("remote media track unmuted", callId, undefined, {
                    context,
                    answerElapsedMs: this.answerElapsedMs(callId),
                    kind: event.track?.kind,
                    readyState: event.track?.readyState
                });
                console.info("[ETS][early-media] remote audio track unmuted", {
                    callId,
                    context,
                    track: {
                        kind: event.track?.kind,
                        id: event.track?.id,
                        enabled: event.track?.enabled,
                        muted: event.track?.muted,
                        readyState: event.track?.readyState
                    }
                });
                this.clearEarlyMediaFallbackTimer(callId, "remote-track-unmuted");
                this.stopLocalRingback(callId, "remote-track-unmuted");
            });

            event.track?.addEventListener("mute", () => {
                console.info("[ETS][early-media] remote audio track muted", {
                    callId,
                    context,
                    track: {
                        kind: event.track?.kind,
                        id: event.track?.id,
                        enabled: event.track?.enabled,
                        muted: event.track?.muted,
                        readyState: event.track?.readyState
                    }
                });
            });
        });

        pc.addEventListener("iceconnectionstatechange", () => {
            this.markAnswerTiming("ICE connection state changed", callId, undefined, {
                context,
                answerElapsedMs: this.answerElapsedMs(callId),
                iceConnectionState: pc.iceConnectionState
            });
            console.info("[ETS][early-media] peerConnection iceconnectionstatechange", {
                callId,
                context,
                iceConnectionState: pc.iceConnectionState,
                pc: this.describePeerConnection(pc)
            });

            if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
                this.clearEarlyMediaFallbackTimer(callId, `ice-${pc.iceConnectionState}`);
                this.stopLocalRingback(callId, `ice-${pc.iceConnectionState}`);
            }
        });

        pc.addEventListener("connectionstatechange", () => {
            this.markAnswerTiming("peer connection state changed", callId, undefined, {
                context,
                answerElapsedMs: this.answerElapsedMs(callId),
                connectionState: pc.connectionState
            });
            console.info("[ETS][early-media] peerConnection connectionstatechange", {
                callId,
                context,
                connectionState: pc.connectionState,
                pc: this.describePeerConnection(pc)
            });

            if (pc.connectionState === "connected") {
                this.clearEarlyMediaFallbackTimer(callId, "pc-connected");
                this.stopLocalRingback(callId, "pc-connected");
            }
        });

        pc.addEventListener("signalingstatechange", () => {
            console.info("[ETS][early-media] peerConnection signalingstatechange", {
                callId,
                context,
                signalingState: pc.signalingState,
                pc: this.describePeerConnection(pc)
            });
        });
    }

    private logSessionMediaDiagnostics(session: any, callId: string, context: string) {
        try {
            const pc: RTCPeerConnection | undefined = session?.sessionDescriptionHandler?.peerConnection;
            if (pc) this.wirePeerConnectionDiagnostics(pc, callId, context);

            console.info("[ETS][early-media] session media diagnostics", {
                callId,
                context,
                sessionState: String(session?.state ?? "unknown"),
                answerElapsedMs: this.answerElapsedMs(callId),
                hasSessionDescriptionHandler: !!session?.sessionDescriptionHandler,
                hasPeerConnection: !!pc,
                pc: this.describePeerConnection(pc)
            });
        } catch (err) {
            console.warn("[ETS][early-media] session media diagnostics failed", { callId, context, err });
        }
    }

    private answerElapsedMs(callId: string): number | null {
        const startedAt = this.answerStartedAt.get(callId);
        return startedAt === undefined ? null : Math.round(performance.now() - startedAt);
    }

    private attachRemoteAudio(session: any, callId = "unknown", context = "attachRemoteAudio") {
        const audioId = this.audioId || "ets-audio";
        const el = document.getElementById(audioId) as HTMLAudioElement | null;

        this.markAnswerTiming("remote media attach entered", callId, session, {
            context,
            answerElapsedMs: this.answerElapsedMs(callId)
        });

        if (!el) {
            console.warn("[ETS][early-media] attachRemoteAudio: audio element not found", { callId, context, audioId });
            return;
        }

        const sdh = session.sessionDescriptionHandler;
        const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
        if (!pc) {
            console.warn("[ETS][early-media] attachRemoteAudio: peerConnection not available yet", {
                callId,
                context,
                audioId,
                hasSessionDescriptionHandler: !!sdh,
                audio: {
                    muted: el.muted,
                    paused: el.paused,
                    readyState: el.readyState,
                    srcObject: !!(el as any).srcObject
                }
            });
            return;
        }

        this.wirePeerConnectionDiagnostics(pc, callId, context);
        console.info("[ETS][early-media] attachRemoteAudio starting", {
            callId,
            context,
            audioId,
            audioBefore: {
                muted: el.muted,
                paused: el.paused,
                readyState: el.readyState,
                autoplay: el.autoplay,
                srcObject: !!(el as any).srcObject
            },
            pc: this.describePeerConnection(pc)
        });

        const remoteStream = new MediaStream();
        pc.getReceivers().forEach((r: any) => {
            if (r.track) remoteStream.addTrack(r.track);
        });

        (el as any).srcObject = remoteStream;
        el.autoplay = true;
        el.muted = false;

        this.markAnswerTiming("remote media attached", callId, session, {
            context,
            answerElapsedMs: this.answerElapsedMs(callId),
            trackCount: remoteStream.getTracks().length
        });

        const p = el.play?.();
        if (p && typeof (p as any).catch === "function") {
            (p as any)
                .then(() => {
                    this.markAnswerTiming("remote audio play resolved", callId, session, {
                        context,
                        answerElapsedMs: this.answerElapsedMs(callId),
                        readyState: el.readyState,
                        trackCount: remoteStream.getTracks().length
                    });
                    console.info("[ETS][early-media] audio play() resolved", {
                        callId,
                        context,
                        audioId,
                        muted: el.muted,
                        paused: el.paused,
                        readyState: el.readyState,
                        trackCount: remoteStream.getTracks().length
                    });
                })
                .catch((err: any) => {
                    this.markAnswerTiming("remote audio play failed", callId, session, {
                        context,
                        answerElapsedMs: this.answerElapsedMs(callId),
                        name: err?.name ?? null,
                        error: err?.message ?? String(err)
                    });
                    console.warn("[ETS][early-media] audio play() blocked/failed", {
                        callId,
                        context,
                        audioId,
                        err: err?.message ?? String(err),
                        name: err?.name ?? null,
                        muted: el.muted,
                        paused: el.paused,
                        readyState: el.readyState,
                        trackCount: remoteStream.getTracks().length
                    });
                });
        } else {
            console.info("[ETS][early-media] audio play() did not return a promise", { callId, context, audioId });
        }

        console.info("[ETS][early-media] attachRemoteAudio: attached remote stream", {
            callId,
            context,
            audioId,
            tracks: remoteStream.getTracks().map((t) => ({
                kind: t.kind,
                id: t.id,
                enabled: t.enabled,
                muted: t.muted,
                readyState: t.readyState
            })),
            audioAfter: {
                muted: el.muted,
                paused: el.paused,
                readyState: el.readyState,
                srcObject: !!(el as any).srcObject
            }
        });
    }

    private async hardReset(reason: string, invalidateConnect = true) {
        console.warn("[ETS] hardReset()", { reason });
        if (invalidateConnect) {
            ++this.connectOperation;
            this.connectInFlight = undefined;
            this.connectRequestedCfgKey = undefined;
        }
        ++this.transportEpoch;
        this.cancelRegistrationAttempt?.();
        this.registrationInFlight = undefined;
        const ua = this.ua;
        const registerer = this.reg;
        const subscribers = [...this.blfSubscribers.values()];
        const voicemail = this.voicemailSubscriber;
        // Revoke old callbacks before any asynchronous cleanup. A newer
        // profile may already be connecting when the old UA finishes stopping.
        this.ua = undefined;
        this.reg = undefined;
        this.regState = undefined;
        this.lastCfg = undefined;
        this.registrationTransportEpoch = -1;
        this.blfSubscribers.clear();
        this.voicemailSubscriber = undefined;
        for (const id of this.earlyMediaFallbackTimers.keys()) this.clearEarlyMediaFallbackTimer(id, "hard-reset");
        this.outboundEarlyMediaCalls.clear();
        for (const id of this.ringbackByCallId.keys()) this.stopLocalRingback(id);
        this.calls.clear();
        this.incomingCalls.clear();
        this.answerInFlight.clear();
        this.callMeta.clear();
        this.activeCallId = undefined;
        this.unregisterRequested = false;
        this.emitTransport("disconnected");
        this.emitRegistration("unregistered");
        const previousReset = this.resetInFlight;
        const cleanup = (async () => {
            if (previousReset) await previousReset;
            try { await registerer?.unregister(); } catch { /* best effort */ }
            try { await ua?.stop(); } catch { /* best effort */ }
            for (const subscriber of subscribers) {
                try { await subscriber.unsubscribe?.(); } catch { /* best effort */ }
            }
            try { await voicemail?.unsubscribe?.(); } catch { /* best effort */ }
        })();
        this.resetInFlight = cleanup;
        try { await cleanup; }
        finally { if (this.resetInFlight === cleanup) this.resetInFlight = undefined; }
    }
}
